import { nextActionToTakeEnum } from "@entities/brief/lib/constants";
import { api } from "@shared/lib/api";
import { IBriefInfluencerV2 } from "@shared/lib/types/briefInfluencerV2";
import {
  IBulkPaymentRequest,
  IFinanceApprovalLot,
  IFinanceApprovalLotsRequest,
  IFinanceApprovalLotsResponse,
  IGstFileUploadRequest,
  IGstInvoices,
  IGstInvoicesRequest,
} from "@shared/lib/types/finance";
import {
  BackednResponse,
  IBackendResponseError,
} from "@shared/lib/types/response";
import { ICampaignAlterationApprovalRequest } from "../../../campaigns/lib/types";

export class FinanceApprovalService {
  public static async getFinanceApprovalLots(
    data: IFinanceApprovalLotsRequest
  ) {
    const url = `brief/influencer-v2/purchase-lots/search`;
    const lots = (await api
      .post(url, { json: data })
      .json()) as BackednResponse<IFinanceApprovalLotsResponse>;
    return lots;
  }

  public static async getFinanceApprovalLot(lotId: string) {
    const url = `brief/influencer-v2/purchase-lots?purchaseLotId=${lotId}`;
    const lot = (await api
      .get(url)
      .json()) as BackednResponse<IFinanceApprovalLot>;
    return lot;
  }

  public static async approveFinanceApprovalLotIds(
    purchaseLotId: string,
    purchaseLotItemsIds: string[]
  ) {
    const url = `brief/influencer-v2/purchase-lots/approve`;
    const lot = (await api
      .post(url, { json: { purchaseLotId, purchaseLotItemsIds } })
      .json()) as BackednResponse<{
      nextActionToTake: nextActionToTakeEnum;
      result: {
        status: string;
        reason: string;
        briefInfluencer: IBriefInfluencerV2;
        purchaseLotId: string;
        purchaseLotItemId: string;
      }[];
    }>;
    return lot;
  }

  public static async rejectFinanceApprovalLotIds(
    purchaseLotId: string,
    purchaseLotItemsIds: string[],
    rejectReason: string
  ) {
    const url = `brief/influencer-v2/purchase-lots/reject`;
    const lot = (await api
      .post(url, { json: { purchaseLotId, purchaseLotItemsIds, rejectReason } })
      .json()) as BackednResponse<void>;
    return lot;
  }

  public static async addToFinanceLots(
    briefInfluencerIds: string[],
    briefId: string,
    remarks: string,
    purchaseProof: string
  ) {
    const url = `brief/influencer-v2/purchase-lots`;
    const lot = (await api
      .put(url, {
        json: { briefInfluencerIds, briefId, remarks, purchaseProof },
      })
      .json()) as IBackendResponseError<
      void,
      {
        recurrsiveError: {
          data: {
            status: "failed" | "success";
            reason: string;
            briefInfluencer: IBriefInfluencerV2;
          }[];
        };
      }
    >;
    return lot;
  }

  public static async purchaseNonProfitableLot(
    purchaseLotId: string,
    purchaseLotItemsId: string
  ) {
    const url =
      "brief/influencer-v2/purchase-lots/approve-non-profitable-trade";
    await api.post(url, {
      json: {
        purchaseLotId,
        purchaseLotItemsId,
      },
    });
  }
  public static async getGstInvoices(data: IGstInvoicesRequest) {
    const url = `billing/vendor/invoice/search`;
    const invoices = (await api
      .post(url, {
        json: {
          ...data,
          gstAmount: {
            from: 0.01,
          },
        },
      })
      .json()) as BackednResponse<IGstInvoices>;
    return invoices;
  }

  public static async uploadGstFile(data: IGstFileUploadRequest) {
    const url = `download/vendorInvoiceGstFilling`;
    const invoice = (await api
      .post(url, { json: data })
      .json()) as BackednResponse<void>;
    return invoice;
  }

  public static async bulkPayments(data: {
    vendorPaymentsRequests: IBulkPaymentRequest[];
    token: string;
    otp: string;
  }) {
    const url = `billing/vendor/payments/bulk-requests`;
    const payment = (await api
      .post(url, { json: { ...data } })
      .json()) as BackednResponse<void>;
    return payment;
  }

  public static async sendBulkPaymentsOtp() {
    const url = `billing/vendor/payments/bulk-requests/send-otp`;
    const payment = (await api.post(url).json()) as BackednResponse<{
      token: string;
    }>;
    return payment;
  }

  public static async downloadGstInvoices(data: {
    invoiceDate: {
      from: string;
      to: string;
    };
  }) {
    const url = `download/vendorInvoiceTdsFilling`;
    const invoices = (await api
      .post(url, { json: data })
      .json()) as BackednResponse<void>;
    return invoices;
  }

  public static async searchAlterationRequests(data: {
    searchQuery: string;
    status: string[];
    limit: number;
    pageNo: number;
    campaignInfluencerIds?: string[];
  }): Promise<
    BackednResponse<{
      data: ICampaignAlterationApprovalRequest[];
      pagination: { totalCount: number };
    }>
  > {
    const url = `campaign/influencer/alteration/search`;
    const response = (await api
      .post(url, { json: data })
      .json()) as BackednResponse<{
      data: ICampaignAlterationApprovalRequest[];
      pagination: { totalCount: number };
    }>;
    return response;
  }

  public static async approveAlteration(alterationLotId: string) {
    const url = `campaign/influencer/alteration/approve`;
    return await api.put(url, {
      json: {
        alterationLotId,
      },
    });
  }

  public static async rejectAlteration(
    alterationLotId: string,
    rejectReason: string
  ) {
    const url = `campaign/influencer/alteration/reject`;
    return await api.put(url, {
      json: {
        alterationLotId,
        rejectReason,
      },
    });
  }
}
